// API funcional tipada — wrapper liviano sobre request().
// Para tipos completos auto-generados, correr `pnpm api:gen` y reemplazar imports por @/lib/api/gen.

import { request } from "./http";

export type MeOut = {
  id: number;
  email: string;
  name: string;
  role: string;
  account_id: number | null;
  account_name: string | null;
  account_type: string | null;
  plan_code: string | null;
  needs_onboarding: boolean;
};

export type DashboardSummary = {
  range_days: number;
  total_mentions: number;
  delta: number;
  delta_pct: number | null;
  sentiment: { pos: number; neg: number; neu: number; nss: number };
  reach: number;
  top_sources: Array<{ network: string; count: number }>;
  daily: Array<{ day: string; count: number }>;
};

export type NetworkBreakdown = {
  network: string;
  volume: number;
  reach: number;
  engagement: number;
  engagement_rate: number;
  share: number;
  sentiment: {
    pos: number; neg: number; neu: number;
    nss: number; pos_pct: number; neu_pct: number; neg_pct: number;
  };
  daily: Array<{ day: string; count: number }>;
};

export type TopNetworkPost = {
  id: number;
  author_handle: string | null;
  text: string;
  reach: number;
  engagement: number;
  sentiment: string | null;
  url: string | null;
  published_at: string;
};

export type ProjectAnalytics = {
  project_id: number;
  project_name: string;
  project_networks: string[];
  range_days: number;
  total_mentions: number;
  delta: number;
  delta_pct: number | null;
  reach: number;
  engagement: number;
  engagement_rate: number;
  sentiment: { pos: number; neg: number; neu: number; nss: number };
  per_network: NetworkBreakdown[];
  daily: Array<{ day: string; count: number }>;
  top_per_network: Record<string, TopNetworkPost>;
};

export type MentionOut = {
  id: number;
  project_id: number | null;
  network: string;
  author_handle: string | null;
  text: string;
  url: string | null;
  lang: string | null;
  sentiment: "pos" | "neg" | "neu" | null;
  sentiment_score: number | null;
  reach: number;
  engagement: number;
  content_type: string | null;
  country: string | null;
  published_at: string;
  captured_at: string;
  tags: string[];
};

export type ProjectOut = {
  id: number;
  name: string;
  description: string | null;
  folder_id: number | null;
  color: string | null;
  created_at: string;
  mentions_count: number;
  last_mention_at: string | null;
  networks: string[];
  network_queries: Record<string, string>;
  client_ids: number[];
};

export const REDES_DISPONIBLES = [
  { code: "x", label: "X (Twitter)", implementado: true },
  { code: "ig", label: "Instagram", implementado: false },
  { code: "fb", label: "Facebook", implementado: false },
  { code: "li", label: "LinkedIn", implementado: false },
  { code: "tt", label: "TikTok", implementado: false },
  { code: "yt", label: "YouTube", implementado: true },
  { code: "news", label: "News (RSS)", implementado: true },
  { code: "reddit", label: "Reddit", implementado: true },
  { code: "reviews", label: "Reviews", implementado: false },
] as const;

export type ConversationOut = {
  id: number;
  network: string;
  status: string;
  assignee_id: number | null;
  unread_count: number;
  contact: { handle: string; display_name: string | null; avatar_url: string | null } | null;
};

export type AccountOut = {
  id: number;
  name: string;
  type: string;
  status: string;
  tz: string;
  plan: {
    code: string;
    name: string;
    mentions_quota: number;
    projects_quota: number;
    profiles_analytic_quota: number;
    profiles_crm_quota: number;
    users_quota: number;
    history_months: number;
  } | null;
  brand_config: Record<string, unknown>;
};

export type UsageOut = {
  month: string;
  mentions: { consumed: number; quota: number; percent: number };
  projects: { used: number; quota: number };
  profiles_analytic: { used: number; quota: number };
  profiles_crm: { used: number; quota: number };
  users: { used: number; quota: number };
};

export const api = {
  // Auth
  login: (email: string, password: string, remember = true) =>
    request<{ status: string }>("/auth/login", { method: "POST", body: { email, password, remember } }),
  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),
  register: (payload: {
    name: string; last_name?: string; company: string; email: string; password: string;
  }) => request<{ status: string; user_id: number; account_id: number }>("/auth/register", { method: "POST", body: payload }),
  me: () => request<MeOut>("/auth/me"),
  passwordForgot: (email: string) =>
    request<{ status: string }>("/auth/password/forgot", { method: "POST", body: { email } }),
  passwordReset: (token: string, password: string) =>
    request<{ status: string }>("/auth/password/reset", { method: "POST", body: { token, password } }),

  // Account / usage
  getAccount: () => request<AccountOut>("/account"),
  patchAccount: (body: { name?: string; tz?: string; brand_config?: Record<string, unknown> }) =>
    request<AccountOut>("/account", { method: "PATCH", body }),
  getUsage: () => request<UsageOut>("/account/usage"),

  // Team (usuarios del propio account)
  listTeam: () => request<TeamUserOut[]>("/account/users"),
  inviteUser: (body: { email: string; password: string; name: string; role: string }) =>
    request<TeamUserOut>("/account/users", { method: "POST", body }),
  patchTeamMember: (id: number, body: { name?: string; role?: string; status?: string }) =>
    request<TeamUserOut>(`/account/users/${id}`, { method: "PATCH", body }),
  removeTeamMember: (id: number) => request<void>(`/account/users/${id}`, { method: "DELETE" }),

  // API Keys (del propio account)
  listAccountApiKeys: () =>
    request<Array<{ id: number; name: string; prefix: string; scopes: string[]; last_used_at: string | null; revoked_at: string | null; created_at: string }>>("/account/api-keys"),
  createAccountApiKey: (body: { name: string; scopes?: string[] }) =>
    request<{ id: number; name: string; prefix: string; plaintext: string; scopes: string[]; created_at: string }>("/account/api-keys", { method: "POST", body }),
  revokeAccountApiKey: (id: number) =>
    request<{ id: number; revoked_at: string | null }>(`/account/api-keys/${id}/revoke`, { method: "POST" }),
  deleteAccountApiKey: (id: number) =>
    request<void>(`/account/api-keys/${id}`, { method: "DELETE" }),

  // Projects / Folders / Profiles
  listProjects: (query?: { folder_id?: number; q?: string; limit?: number; offset?: number }) =>
    request<ProjectOut[]>("/projects", { query }),
  createProject: (body: {
    name: string; description?: string; folder_id?: number; color?: string;
    keywords?: Array<{ expression: string; kind?: string; is_exclusion?: boolean }>;
    networks?: string[]; network_queries?: Record<string, string>; client_ids?: number[];
  }) => request<ProjectOut>("/projects", { method: "POST", body }),
  getProject: (id: number) => request<ProjectOut>(`/projects/${id}`),
  patchProject: (id: number, body: Partial<{
    name: string; description: string; folder_id: number | null; color: string;
    networks: string[]; network_queries: Record<string, string>; client_ids: number[];
  }>) => request<ProjectOut>(`/projects/${id}`, { method: "PATCH", body }),
  deleteProject: (id: number) => request<void>(`/projects/${id}`, { method: "DELETE" }),
  listFolders: () => request<Array<{ id: number; name: string; parent_id: number | null }>>("/folders"),
  listProjectProfiles: (projectId: number) =>
    request<Array<{ id: number; network: string; kind: string; handle: string; display_name: string | null; status: string }>>(
      `/projects/${projectId}/profiles`,
    ),
  linkProjectProfile: (projectId: number, profileId: number) =>
    request<{ status: string }>(`/projects/${projectId}/profiles/${profileId}`, { method: "POST" }),
  unlinkProjectProfile: (projectId: number, profileId: number) =>
    request<void>(`/projects/${projectId}/profiles/${profileId}`, { method: "DELETE" }),
  listProfiles: () => request<Array<{ id: number; network: string; kind: string; handle: string; display_name: string | null; avatar_url: string | null; status: string }>>("/profiles"),
  connectProfile: (body: { network: string; kind: "analytic" | "crm"; handle: string; display_name?: string; avatar_url?: string }) =>
    request<{ id: number; network: string; kind: string; handle: string; status: string }>("/profiles", { method: "POST", body }),
  disconnectProfile: (id: number) => request<void>(`/profiles/${id}`, { method: "DELETE" }),

  // Templates (CRM)
  createTemplate: (body: { name: string; body: string; folder?: string; network_scope?: unknown[]; shortcut?: string }) =>
    request<{ id: number; name: string; body: string }>("/templates", { method: "POST", body }),
  deleteTemplate: (id: number) => request<void>(`/templates/${id}`, { method: "DELETE" }),

  // Scheduled posts mutations
  createScheduledPost: (body: { networks: unknown[]; text: string; media?: unknown[]; scheduled_at: string; first_comment?: string; location?: string; status?: string }) =>
    request<{ id: number; status: string }>("/scheduled-posts", { method: "POST", body }),
  cancelScheduledPost: (id: number) => request<void>(`/scheduled-posts/${id}`, { method: "DELETE" }),

  // Mentions / Dashboard
  listMentions: (query?: Record<string, string | number | undefined>) =>
    request<MentionOut[]>("/mentions", { query }),
  getMention: (id: number) => request<MentionOut>(`/mentions/${id}`),
  exportMentionsUrl: (query?: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams(
      Object.entries(query || {})
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return `/api/mentions/export.csv${qs ? `?${qs}` : ""}`;
  },
  dashboardSummary: (days = 7) => request<DashboardSummary>("/dashboard/summary", { query: { days } }),
  projectAnalytics: (projectId: number, days = 30) =>
    request<ProjectAnalytics>(`/dashboard/project/${projectId}/analytics`, { query: { days } }),
  unreadMentions: (limit = 5) =>
    request<Array<{ id: number; network: string; author: string | null; text: string; sentiment: string | null; published_at: string }>>("/dashboard/unread", { query: { limit } }),

  // CRM
  listConversations: (query?: { status?: string; assignee_id?: number; network?: string; limit?: number; offset?: number }) =>
    request<ConversationOut[]>("/conversations", { query }),
  listMessages: (cid: number) =>
    request<Array<{ id: number; direction: string; author_handle: string | null; text: string; is_internal_note: boolean; created_at: string }>>(`/conversations/${cid}/messages`),
  reply: (cid: number, text: string, isNote = false) =>
    request<{ id: number; direction: string; text: string }>(`/conversations/${cid}/reply`, { method: "POST", body: { text, is_internal_note: isNote } }),
  changeConversationStatus: (cid: number, status: string) =>
    request<{ status: string }>(`/conversations/${cid}/status`, { method: "PATCH", body: { status } }),
  listTemplates: () =>
    request<Array<{ id: number; name: string; body: string; variables: Record<string, unknown>; folder: string | null; network_scope: unknown[]; shortcut: string | null }>>("/templates"),

  // Alerts / Reports / Scheduling
  listAlerts: () => request<Array<{ id: number; name: string; type: string; project_id: number | null; threshold: Record<string, unknown>; channels: unknown[]; is_active: boolean }>>("/alerts"),
  listReports: () => request<Array<{ id: number; name: string; format: string; config: Record<string, unknown>; schedule: Record<string, unknown> }>>("/reports"),
  listScheduledPosts: (query?: Record<string, string | number | undefined>) =>
    request<Array<{ id: number; networks: unknown[]; text: string; media: unknown[]; scheduled_at: string; status: string }>>("/scheduled-posts", { query }),

  // ============ OAuth (conectar cuentas RRSS reales) ============
  oauth: {
    listProviders: () =>
      request<Array<{ provider: string; network: string; configured: boolean }>>("/oauth/providers"),
    start: (provider: string) =>
      request<{ authorize_url: string }>(`/oauth/${provider}/start`),
  },

  // ============ Admin (superadmin) ============
  admin: {
    stats: () => request<{ accounts_active: number; accounts_total: number; total_mentions: number; users_count: number }>("/admin/stats"),
    listPlans: () => request<Array<{ id: number; code: string; name: string; mentions_quota: number; projects_quota: number; users_quota: number; price_cents: number; currency: string }>>("/admin/plans"),

    listAccounts: (query?: { q?: string; status?: string; limit?: number; offset?: number }) =>
      request<AdminAccountOut[]>("/admin/accounts", { query: query as Record<string, string | number | undefined> }),
    getAccount: (id: number) => request<AdminAccountOut>(`/admin/accounts/${id}`),
    createAccount: (body: AdminAccountCreateIn) =>
      request<AdminAccountOut>("/admin/accounts", { method: "POST", body }),
    patchAccount: (id: number, body: Partial<{
      name: string; type: string; plan_id: number; tz: string;
      brand_config: Record<string, unknown>;
      description: string; contact_email: string; contact_phone: string;
      address: string; tax_id: string; billing_email: string; website: string;
      default_currency: string;
    }>) =>
      request<AdminAccountOut>(`/admin/accounts/${id}`, { method: "PATCH", body }),
    suspendAccount: (id: number) =>
      request<AdminAccountOut>(`/admin/accounts/${id}/suspend`, { method: "POST" }),
    activateAccount: (id: number) =>
      request<AdminAccountOut>(`/admin/accounts/${id}/activate`, { method: "POST" }),
    deleteAccount: (id: number) =>
      request<void>(`/admin/accounts/${id}`, { method: "DELETE" }),

    // Módulo Medios Tradicionales — bootstrap desde superadmin
    seedMediaOutlets: (accountId: number) =>
      request<{ status: string; created: number; account_id: number }>(
        `/admin/accounts/${accountId}/seed-media-outlets`, { method: "POST" }
      ),
    seedMediaDemo: (accountId: number) =>
      request<{ client_id: number; client_name: string; campaigns_created: number;
        ads_created: number; placements_created: number; evidences_created: number;
        report_id: number; report_share_token: string; report_url: string;
        client_portal_token: string; client_portal_url: string }>(
        `/admin/accounts/${accountId}/seed-media-demo`, { method: "POST" }
      ),
    loadPeruGeoFull: () =>
      request<{ status: string; regions: number; provinces: number; districts: number; mode: string }>(
        "/admin/peru-geo/load-full", { method: "POST" }
      ),
    peruGeoStatus: () => request<AdminPeruGeoStatus>("/admin/peru-geo/status"),

    // Catálogo global de tipos de medio (Medios Tradicionales)
    listOutletKinds: (query?: { is_active?: boolean }) =>
      request<OutletKindOut[]>("/admin/outlet-kinds", { query }),
    createOutletKind: (body: { code: string; name: string; description?: string; accepts_formats?: string[]; sort_order?: number }) =>
      request<OutletKindOut>("/admin/outlet-kinds", { method: "POST", body }),
    patchOutletKind: (id: number, body: Partial<{ code: string; name: string; description: string; accepts_formats: string[]; is_active: boolean; sort_order: number }>) =>
      request<OutletKindOut>(`/admin/outlet-kinds/${id}`, { method: "PATCH", body }),
    deleteOutletKind: (id: number) =>
      request<void>(`/admin/outlet-kinds/${id}`, { method: "DELETE" }),

    listUsers: (accountId: number) =>
      request<AdminUserOut[]>(`/admin/accounts/${accountId}/users`),
    listAllUsers: (query?: { q?: string; account_id?: number; limit?: number; offset?: number }) =>
      request<AdminUserWithAccountOut[]>("/admin/users", { query: query as Record<string, string | number | undefined> }),
    listAllProjects: (query?: { q?: string; account_id?: number; limit?: number; offset?: number }) =>
      request<AdminProjectWithAccountOut[]>("/admin/projects", { query: query as Record<string, string | number | undefined> }),
    createSuperadmin: (body: { email: string; password: string; name: string }) =>
      request<AdminUserOut>("/admin/superadmins", { method: "POST", body }),
    createUser: (accountId: number, body: AdminUserCreateIn) =>
      request<AdminUserOut>(`/admin/accounts/${accountId}/users`, { method: "POST", body }),
    patchUser: (userId: number, body: Partial<{ name: string; email: string; password: string; role: string; status: string }>) =>
      request<AdminUserOut>(`/admin/users/${userId}`, { method: "PATCH", body }),
    deleteUser: (userId: number) =>
      request<void>(`/admin/users/${userId}`, { method: "DELETE" }),

    listApiKeys: (accountId: number) =>
      request<AdminApiKeyOut[]>(`/admin/accounts/${accountId}/api-keys`),
    createApiKey: (accountId: number, body: { name: string; scopes?: string[] }) =>
      request<AdminApiKeyCreatedOut>(`/admin/accounts/${accountId}/api-keys`, { method: "POST", body }),
    revokeApiKey: (id: number) =>
      request<AdminApiKeyOut>(`/admin/api-keys/${id}/revoke`, { method: "POST" }),
    deleteApiKey: (id: number) =>
      request<void>(`/admin/api-keys/${id}`, { method: "DELETE" }),

    resetUserPassword: (userId: number) =>
      request<{ temp_password: string; email: string }>(`/admin/users/${userId}/reset-password`, { method: "POST" }),
    impersonateUser: (userId: number) =>
      request<{ session_token: string; target_user_id: number; expires_in_hours: number }>(`/admin/users/${userId}/impersonate`, { method: "POST" }),

    auditLog: (query?: { account_id?: number; action?: string; limit?: number; offset?: number }) =>
      request<Array<{ id: number; at: string; account_id: number | null; user_id: number | null; action: string; resource_type: string | null; resource_id: number | null; payload: Record<string, unknown>; ip: string | null }>>(
        "/admin/audit-log",
        { query: query as Record<string, string | number | undefined> },
      ),

    billingStats: () =>
      request<{ accounts_by_status: Array<{ status: string; count: number }>; accounts_by_plan: Array<{ plan_code: string | null; plan_name: string | null; price_cents: number; currency: string; count: number }>; mrr_cents: number; mrr_usd: number }>("/admin/billing-stats"),

    getPlatformSettings: () => request<PlatformSettingsOut>("/admin/platform-settings"),
    patchPlatformSettings: (body: Partial<PlatformSettingsOut>) =>
      request<PlatformSettingsOut>("/admin/platform-settings", { method: "PATCH", body }),
    uploadBrandingAsset: async (kind: "logo" | "favicon", file: File): Promise<{ url: string; kind: string }> => {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch(`/api/admin/platform-settings/upload?kind=${kind}`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
      return resp.json();
    },
  },
};

export type LoginStat = { n: string; l: string };

export type PlatformSettingsOut = {
  brand_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  email_from_name: string | null;
  email_footer_html: string | null;
  support_email: string | null;
  pwa_short_name: string | null;
  login_tagline: string | null;
  login_headline: string | null;
  login_subtitle: string | null;
  login_stats: LoginStat[];
  login_compliance: string | null;
  login_register_tagline: string | null;
  updated_at: string;
};

// ============ Tipos admin ============

export type AdminAccountOut = {
  id: number;
  name: string;
  type: string;
  status: string;
  tz: string;
  plan_id: number | null;
  plan_code: string | null;
  brand_config: Record<string, unknown>;
  created_at: string;
  users_count: number;
  projects_count: number;
  // Info contacto + billing
  description: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  tax_id: string | null;
  billing_email: string | null;
  website: string | null;
  default_currency: string;
};

export type AdminAccountCreateIn = {
  name: string;
  type?: "administrada" | "autoservicio" | "agencia";
  plan_id?: number | null;
  tz?: string;
  brand_config?: Record<string, unknown>;
  owner_email: string;
  owner_password: string;
  owner_name: string;
  // Bootstrap módulo Medios Tradicionales (solo cuando type='agencia').
  seed_media_outlets?: boolean;
  seed_peru_geo_full?: boolean;
};

export type AdminPeruGeoStatus = {
  regions: number;
  provinces: number;
  districts: number;
  mode: "full" | "starter" | "empty";
};

export type AdminUserOut = {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  last_login_at: string | null;
  created_at: string;
};

export type AdminUserCreateIn = {
  email: string;
  password: string;
  name: string;
  role: "owner" | "admin" | "editor" | "analyst" | "responder" | "viewer" | "superadmin";
};

export type AdminApiKeyOut = {
  id: number;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
};

export type AdminApiKeyCreatedOut = AdminApiKeyOut & { plaintext: string };

export type AdminUserWithAccountOut = AdminUserOut & {
  account_id: number | null;
  account_name: string | null;
};

export type AdminProjectWithAccountOut = {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  account_id: number;
  account_name: string;
  mentions_count: number;
};

export type TeamUserOut = {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  last_login_at: string | null;
  created_at: string;
};

// ============================================================
// Módulo Medios Tradicionales (HOLACLIENTE Agencia de Medios)
// ============================================================

// Los tipos de medio son DB-driven (catálogo global gestionado por superadmin).
// Se mantiene el alias por compatibilidad; los codes seed viven en OUTLET_KIND_LABELS.
export type OutletKind = string;

export type OutletKindOut = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  accepts_formats: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
};

export type Coverage = "nacional" | "regional" | "local" | "zona";

export type AdType =
  | "interrupcion_programada" | "fuerza_mayor_emergencia"
  | "fe_erratas" | "junta_accionistas" | "declaracion_jurada"
  | "comunicado_institucional" | "publicidad_general" | "aviso_corte";

export type PlacementState = "programado" | "finalizado" | "realizado" | "cancelado";
export type ContentFormat = "1/8" | "1/4" | "1/2" | "full_page" | "30s" | "60s" | "90s" | "custom";
export type EvidenceStage = "solicitud" | "programacion" | "contenido" | "culminacion" | "facturacion";
export type FileKind = "pdf" | "jpeg" | "png" | "mp3" | "mp4";
export type ReportKind = "detallado" | "resumen";
export type ReportFormat = "pdf" | "xlsx" | "pptx";

export type AgencyClientOut = {
  id: number; name: string; code: string; slug: string;
  ruc: string | null; contact_name: string | null; contact_email: string | null;
  phone: string | null; logo_url: string | null; industry: string | null;
  is_active: boolean; notes: string | null; ad_counter: number; created_at: string;
  // Portal de transparencia por cliente (enlace único y estable)
  portal_enabled: boolean;
  portal_token: string | null;
  portal_published_at: string | null;
  portal_expires_at: string | null;
  // Credenciales del portal (el hash nunca viaja)
  portal_access_user: string | null;
  portal_login_required: boolean;
};

export type BudgetSegmentScope = "media_kind" | "outlet";

export type ClientBudgetSegment = {
  id?: number;
  scope: BudgetSegmentScope;
  outlet_kind: OutletKind | null;
  outlet_id: number | null;
  amount: number;
};

export type ClientBudgetOut = {
  id: number; account_id: number; client_id: number;
  total_amount: number | null; currency: string; notes: string | null;
  segments: ClientBudgetSegment[]; created_at: string;
};

export type BudgetSegmentConsumption = {
  scope: BudgetSegmentScope; outlet_kind: string | null; outlet_id: number | null;
  label: string; amount: number; spent: number; pct: number;
};

export type ClientBudgetConsumption = {
  currency: string;
  total_amount: number | null; total_spent: number; total_pct: number | null;
  segments: BudgetSegmentConsumption[];
  by_outlet_kind: Record<string, number>;
  by_outlet: Array<{ outlet_id: number; name: string; kind: string; spent: number }>;
};

export type BudgetOverviewRow = {
  client_id: number; client_name: string; client_code: string;
  currency: string;
  budget_total: number | null;
  spent: number;            // precio comprometido (consume presupuesto)
  pct: number | null;
  cost: number;             // costo interno agencia
  margin: number;
  cost_missing: number;     // placements comprometidos sin costo registrado
  has_budget: boolean;
};

export type BudgetsOverviewOut = {
  rows: BudgetOverviewRow[];
  totals_by_currency: Record<string, { budget_total: number; spent: number; cost: number; margin: number }>;
};

export type OutletOut = {
  id: number; name: string; kind: string; region_id: number | null;
  coverage: string | null; website: string | null;
  frequency_mhz: number | null; circulation: number | null;
  accepts_formats: string[]; is_active: boolean; notes: string | null;
  // Tarifario (rate card)
  rate_base_amount: number | null;
  rate_per_word: number | null;
  rate_per_second: number | null;
  rate_per_minute: number | null;
  rate_per_repetition: number | null;
  rate_apply_repetitions: boolean;
  rate_currency: string;
  rate_notes: string | null;
  created_at: string;
};

export type PriceEstimateOut = {
  outlet_id: number;
  cost_amount: number | null;
  currency: string;
  breakdown: Record<string, unknown>;
};

export type RegionOut = { id: number; ubigeo: string; name: string };
export type ProvinceOut = { id: number; ubigeo: string; region_id: number; name: string };
export type DistrictOut = { id: number; ubigeo: string; province_id: number; name: string };

export type CampaignOut = {
  id: number; client_id: number; name: string; code: string | null;
  description: string | null; period_start: string | null; period_end: string | null;
  status: string; budget_amount: number | null; currency: string;
  folder_id: number | null; created_by: number | null; created_at: string;
};

export type CampaignSummaryOut = {
  campaign_id: number; total_ads: number; total_placements: number;
  by_state: Record<string, number>;
  by_coverage: Record<string, number>;
  by_outlet_kind: Record<string, number>;
  compliance_pct: number;
};

export type CampaignDeleteImpact = {
  campaign_id: number; ads: number; placements: number; evidences: number;
  reports: number; report_assets: number; report_indicators: number;
};

export type PlacementDeleteImpact = {
  placement_id: number; evidences: number;
  report_assets: number; report_indicators: number;
};

export type AdPriority = "baja" | "normal" | "alta" | "urgente";

export type AdTypeOut = {
  id: number; code: string; name: string;
  description: string | null; is_active: boolean; sort_order: number; created_at: string;
};

export type AdCategoryOut = {
  id: number; code: string; name: string;
  description: string | null; color: string | null;
  is_active: boolean; sort_order: number; created_at: string;
};

export type AdOut = {
  id: number; campaign_id: number; code: string; title: string;
  ad_type: string; category_id: number | null;
  summary: string | null; creative_url: string | null;
  received_at: string | null; requested_at: string | null;
  requested_by_text: string | null;
  request_responsible_name: string | null;
  request_responsible_email: string | null;
  request_responsible_phone: string | null;
  request_responsible_department: string | null;
  request_reference: string | null;
  priority: string;
  status: string;
  tags: string[];
  internal_notes: string | null;
  extra: Record<string, unknown>;
  created_by: number | null; created_at: string;
};

export type PlacementOut = {
  id: number; ad_id: number; outlet_id: number; coverage: string;
  region_id: number | null; province_id: number | null; district_id: number | null;
  requested_at: string | null; scheduled_at: string | null; published_at: string | null;
  end_at: string | null; actual_end_at: string | null; payment_at: string | null;
  duration_days: number | null; repetitions: number;
  spot_duration_seconds: number | null; word_count: number | null;
  content_format: string | null; state: string;
  cost_amount: number | null; price_amount: number | null; notes: string | null;
  extra: Record<string, unknown>; created_at: string;
};

export type EvidenceOut = {
  id: number; placement_id: number; stage: string;
  file_url: string; file_name: string | null; file_size: number | null;
  mime_type: string | null; file_kind: string | null;
  uploaded_by: number | null; caption: string | null;
  description: string | null; tags: string[]; is_public: boolean; extra: Record<string, unknown>;
  created_at: string;
};

export type EvidenceUploadMeta = {
  stage: EvidenceStage;
  caption?: string;
  description?: string;
  tags?: string[];
  is_public?: boolean;
  extra?: Record<string, unknown>;
};

export type MediaReportOut = {
  id: number; name: string; kind: string; format: string;
  client_id: number | null; filters: Record<string, unknown>;
  branding: Record<string, unknown>;
  summary_markdown: string | null;
  cover_image_url: string | null;
  period_label: string | null;
  is_public: boolean;
  published_at: string | null;
  share_token: string | null;
  share_expires_at: string | null;
  created_by: number | null; created_at: string;
};

export type AssetKind = "evidencia" | "anexo" | "transparencia" | "contrato" | "factura" | "auditoria" | "otro";
export type IndicatorKind = "audiencia" | "rating" | "share" | "grp" | "reach" | "frequency" | "cpm" | "impresiones" | "engagement" | "circulacion" | "otro";

export type ReportAssetOut = {
  id: number; report_id: number;
  campaign_id: number | null; ad_id: number | null; placement_id: number | null;
  kind: string; title: string;
  description: string | null; file_url: string; file_name: string | null;
  file_size: number | null; mime_type: string | null; file_kind: string | null;
  is_public: boolean; sort_order: number; uploaded_by: number | null;
  extra: Record<string, unknown>; created_at: string;
};

export type ReportIndicatorOut = {
  id: number; report_id: number;
  campaign_id: number | null; ad_id: number | null; placement_id: number | null;
  kind: string; code: string; label: string;
  value_numeric: number | null; value_text: string | null;
  unit: string | null; source: string | null; measured_at: string | null;
  sort_order: number; notes: string | null; is_public: boolean;
  extra: Record<string, unknown>; created_at: string;
};

/** Selección campaña→aviso→placement para vincular anexos/indicadores. */
export type ReportTarget = {
  campaign_id?: number;
  ad_id?: number;
  placement_id?: number;
};

export type PortalSummary = {
  total_placements: number; total_ads: number; compliant_placements: number;
  by_state: Record<string, number>; by_coverage: Record<string, number>;
  by_outlet_kind: Record<string, number>; by_ad_type: Record<string, number>;
  by_priority: Record<string, number>; by_category: Record<string, number>;
  by_region: Array<{ ubigeo: string; name: string; region_id: number; placements: number; spending: number }>;
  monthly_series: Array<{ month: string; placements: number; spending: number }>;
  total_spending: number; currency: string;
  compliance_pct: number;
};

export type PortalDocument = {
  id: number; name: string; kind: string;
  period_label: string | null; published_at: string | null; portal_url: string | null;
};

export type PublicReportPayload = {
  // "client" = portal único por cliente (agrega N reportes); "report" = un solo reporte
  portal?: { kind: "client" | "report"; documents_count: number };
  report: {
    id: number; name: string; kind: string; format: string;
    period_label: string | null; summary_markdown: string | null;
    cover_image_url: string | null; published_at: string | null; expires_at: string | null;
  };
  agency: {
    name: string; description: string | null;
    contact_email: string | null; website: string | null;
  };
  client: { name: string | null; code: string | null; logo_url: string | null };
  initial_summary: PortalSummary;
  filtered_summary: PortalSummary | null;
  documents?: PortalDocument[];
  indicators: Array<
    Omit<ReportIndicatorOut, "report_id" | "extra" | "is_public" | "sort_order">
    & { ad_code: string | null; ad_title: string | null }
  >;
  assets: Array<{
    id: number; kind: string; title: string; description: string | null;
    file_name: string | null; file_size: number | null; file_kind: string | null;
    mime_type: string | null; download_url: string;
    ad_id: number | null; placement_id: number | null;
    ad_code: string | null; ad_title: string | null;
  }>;
  runs: Array<{ id: number; status: string; finished_at: string | null; download_url: string }>;
};

export type PortalFilters = {
  date_from?: string;
  date_to?: string;
  state?: string;          // CSV
  ad_type?: string;        // CSV
  outlet_kind?: string;    // CSV
  coverage?: string;       // CSV
  priority?: string;       // CSV
  region_id?: number;
  province_id?: number;
  category_id?: string;    // CSV de IDs
  campaign_id?: string;    // CSV de IDs de campaña (pestaña Publicaciones)
  outlet_id?: string;      // CSV de IDs
  tags?: string;           // CSV any-match en ad.tags
  has_report?: boolean;    // pestaña Publicaciones: con/sin reporte publicado
  evidence_tags?: string;  // CSV en evidence.tags
  evidence_stages?: string; // CSV (solicitud|programacion|contenido|culminacion)
  has_evidence?: boolean;
  price_min?: number;
  price_max?: number;
  q?: string;              // texto libre
};

export type PortalFilterOptions = {
  states: string[];
  ad_types: string[];
  outlet_kinds: string[];
  // Etiquetas del catálogo global de tipos de medio {code: label}
  outlet_kind_labels?: Record<string, string>;
  coverages: string[];
  priorities: string[];
  outlets: Array<{ id: number; name: string; kind: string }>;
  categories: Array<{ id: number; name: string; code: string; color: string | null }>;
  regions: Array<{ id: number; ubigeo: string; name: string }>;
  ad_tags: string[];
  evidence_tags: string[];
  evidence_stages: string[];
  campaigns: Array<{ id: number; name: string; code: string | null; status: string }>;
  price_range: { min: number | null; max: number | null };
  date_range: { min: string | null; max: string | null };
  total_placements: number;
};

export type PortalPlacementRow = {
  id: number;
  ad_code: string; ad_title: string; ad_type: string;
  priority: string | null; category: string | null; tags: string[];
  outlet_name: string; outlet_kind: string; coverage: string;
  region: string | null; province: string | null;
  scheduled_at: string | null; published_at: string | null; end_at: string | null;
  duration_days: number | null; repetitions: number;
  spot_duration_seconds: number | null; word_count: number | null;
  state: string; price_amount: number | null;
  evidence_counts: Record<string, number>;
  evidences: Array<{
    id: number; stage: string;
    file_name: string | null; file_kind: string | null; caption: string | null;
    download_url: string;
  }>;
};

export type PortalPlacementsPage = {
  total: number; limit: number; offset: number;
  items: PortalPlacementRow[];
};

// Fila de la pestaña "Publicaciones" (aviso-céntrica)
export type PortalAdRow = {
  ad_id: number; ad_code: string; ad_title: string; ad_type: string;
  priority: string | null; category: string | null; category_color: string | null;
  tags: string[]; summary: string | null;
  campaign_id: number; campaign_name: string | null;
  campaign_status: string | null; campaign_code: string | null;
  placements_total: number; by_state: Record<string, number>;
  outlets: string[];
  first_scheduled_at: string | null; last_scheduled_at: string | null;
  last_published_at: string | null;
  total_price: number;
  status: "publicado" | "programado" | "cancelado" | "sin_inserciones" | "descartado";
  is_published: boolean;
  reports: Array<{ id: number; name: string; kind: string; period_label: string | null; portal_url: string | null }>;
  annexes: Array<{ id: number; title: string; kind: string; file_kind: string | null; file_name: string | null; download_url: string }>;
};

export type PortalAdsPage = {
  total: number; limit: number; offset: number;
  items: PortalAdRow[];
};

export type MediaReportRunOut = {
  id: number; report_id: number; status: string; file_url: string | null;
  started_at: string | null; finished_at: string | null; error: string | null;
  share_token: string | null; share_expires_at: string | null; created_at: string;
};

export const AD_TYPE_LABELS: Record<AdType, string> = {
  interrupcion_programada: "a) Interrupciones programadas",
  fuerza_mayor_emergencia: "b) Interrupciones por fuerza mayor o emergencia",
  fe_erratas: "c) Fe de erratas o modificaciones",
  junta_accionistas: "d) Avisos de juntas de accionistas",
  declaracion_jurada: "e) Declaraciones juradas",
  comunicado_institucional: "f) Comunicados institucionales y otros avisos normativos",
  publicidad_general: "Publicidad general",
  aviso_corte: "Aviso de corte",
};

export const OUTLET_KIND_LABELS: Record<OutletKind, string> = {
  diario_nacional: "Diarios nacionales",
  diario_regional_local: "Diarios regionales y locales",
  radio_aqp_metro: "Radios Arequipa Metropolitana",
  radio_provincias: "Radios en Provincias",
  medios_digitales_rrss: "Medios digitales y redes sociales",
  tv: "Televisión",
  otros: "Otros",
};

// ============ Auth opcional del portal público ============
// Si el cliente configuró credenciales, el backend responde 401 con
// code=portal_auth_required y el portal muestra un login. El key emitido
// (HMAC, TTL limitado) se guarda en sessionStorage por token/slug.

export class PortalAuthRequiredError extends Error {
  info: { title?: string; client_name?: string; needs_username?: boolean };
  constructor(info?: { title?: string; client_name?: string; needs_username?: boolean }) {
    super("portal_auth_required");
    this.name = "PortalAuthRequiredError";
    this.info = info ?? {};
  }
}

const portalKeyStorageKey = (token: string) => `tm_portal_key:${token}`;

export function getPortalKey(token: string): string | null {
  try { return sessionStorage.getItem(portalKeyStorageKey(token)); } catch { return null; }
}
export function setPortalKey(token: string, key: string): void {
  try { sessionStorage.setItem(portalKeyStorageKey(token), key); } catch { /* storage bloqueado */ }
}
export function clearPortalKey(token: string): void {
  try { sessionStorage.removeItem(portalKeyStorageKey(token)); } catch { /* storage bloqueado */ }
}

/** Agrega el portal key como query param (para links de descarga/CSV). */
export function withPortalKey(url: string, token: string): string {
  const key = getPortalKey(token);
  if (!key) return url;
  return `${url}${url.includes("?") ? "&" : "?"}pk=${encodeURIComponent(key)}`;
}

async function portalFetch<T>(token: string, url: string): Promise<T> {
  const key = getPortalKey(token);
  const r = await fetch(url, {
    credentials: "omit",
    headers: key ? { "X-Portal-Key": key } : undefined,
  });
  if (r.status === 401) {
    let info: PortalAuthRequiredError["info"] = {};
    try {
      const body = await r.json();
      if (body && typeof body.detail === "object") info = body.detail;
    } catch { /* cuerpo no-JSON */ }
    clearPortalKey(token); // key vencido o inválido → re-login
    throw new PortalAuthRequiredError(info);
  }
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export const media = {
  // Clients
  listClients: (params: { q?: string; is_active?: boolean; limit?: number; offset?: number } = {}) =>
    request<AgencyClientOut[]>(`/media/clients${qs(params)}`),
  createClient: (body: { name: string; code: string; slug?: string; ruc?: string; contact_name?: string;
    contact_email?: string; phone?: string; logo_url?: string; industry?: string; notes?: string }) =>
    request<AgencyClientOut>("/media/clients", { method: "POST", body }),
  getClient: (id: number) => request<AgencyClientOut>(`/media/clients/${id}`),
  patchClient: (id: number, body: Partial<AgencyClientOut>) =>
    request<AgencyClientOut>(`/media/clients/${id}`, { method: "PATCH", body }),
  deleteClient: (id: number) => request<void>(`/media/clients/${id}`, { method: "DELETE" }),
  listClientUsers: (id: number) => request<TeamUserOut[]>(`/media/clients/${id}/users`),
  inviteClientUser: (id: number, body: { email: string; name: string; role: "cliente" | "supervisor"; password: string }) =>
    request<TeamUserOut>(`/media/clients/${id}/invite`, { method: "POST", body }),
  // Portal de transparencia por cliente (enlace único). expires_in_days=null → permanente.
  publishClientPortal: (id: number, expires_in_days: number | null = null) =>
    request<AgencyClientOut>(`/media/clients/${id}/portal/publish`, { method: "POST", body: { expires_in_days } }),
  unpublishClientPortal: (id: number) =>
    request<AgencyClientOut>(`/media/clients/${id}/portal/unpublish`, { method: "POST" }),
  // Credenciales del portal: con password exige login; password vacío quita el login.
  setClientPortalCredentials: (id: number, body: { username?: string | null; password?: string | null }) =>
    request<AgencyClientOut>(`/media/clients/${id}/portal/credentials`, { method: "POST", body }),
  clientPortalUrl: (portal_token: string) => `/public/reports/${portal_token}`,
  // URL pública limpia basada en el slug del cliente (preferida sobre el token).
  clientPortalUrlBySlug: (slug: string) => `/r/${slug}`,
  // Presupuesto por cliente (interno).
  getClientBudget: (id: number) => request<ClientBudgetOut | null>(`/media/clients/${id}/budget`),
  putClientBudget: (id: number, body: { total_amount: number | null; currency?: string; notes?: string | null;
    segments: ClientBudgetSegment[] }) =>
    request<ClientBudgetOut>(`/media/clients/${id}/budget`, { method: "PUT", body }),
  getClientBudgetConsumption: (id: number) =>
    request<ClientBudgetConsumption>(`/media/clients/${id}/budget/consumption`),
  budgetExportXlsxUrl: (id: number) => `/api/media/clients/${id}/budget/export.xlsx`,
  budgetExportPdfUrl: (id: number) => `/api/media/clients/${id}/budget/export.pdf`,
  conciliationExportXlsxUrl: (id: number) => `/api/media/clients/${id}/placements/export.xlsx`,
  // Consolidado multi-cliente (owner/admin)
  budgetsOverview: () => request<BudgetsOverviewOut>("/media/budgets/overview"),
  budgetsOverviewExportXlsxUrl: () => "/api/media/budgets/overview/export.xlsx",

  // Outlets
  // Catálogo global de tipos de medio (DB-driven, gestionado por superadmin)
  listOutletKinds: (params: { is_active?: boolean } = { is_active: true }) =>
    request<OutletKindOut[]>(`/media/outlets/kinds${qs(params)}`),
  listOutlets: (params: { kind?: string; region_id?: number; coverage?: string; q?: string; is_active?: boolean; limit?: number; offset?: number } = {}) =>
    request<OutletOut[]>(`/media/outlets${qs(params)}`),
  createOutlet: (body: Partial<OutletOut> & { name: string; kind: OutletKind }) =>
    request<OutletOut>("/media/outlets", { method: "POST", body }),
  patchOutlet: (id: number, body: Partial<OutletOut>) =>
    request<OutletOut>(`/media/outlets/${id}`, { method: "PATCH", body }),
  deleteOutlet: (id: number) => request<void>(`/media/outlets/${id}`, { method: "DELETE" }),

  // Peru geo (cacheables)
  listRegions: () => request<RegionOut[]>("/media/geo/regions"),
  listProvinces: (region_id?: number) =>
    request<ProvinceOut[]>(`/media/geo/provinces${qs({ region_id })}`),
  listDistricts: (province_id?: number) =>
    request<DistrictOut[]>(`/media/geo/districts${qs({ province_id })}`),

  // Campaigns
  listCampaigns: (params: { client_id?: number; status?: string; date_from?: string; date_to?: string; q?: string; limit?: number; offset?: number } = {}) =>
    request<CampaignOut[]>(`/media/campaigns${qs(params)}`),
  createCampaign: (body: Partial<CampaignOut> & { client_id: number; name: string }) =>
    request<CampaignOut>("/media/campaigns", { method: "POST", body }),
  getCampaign: (id: number) => request<CampaignOut>(`/media/campaigns/${id}`),
  patchCampaign: (id: number, body: Partial<CampaignOut>) =>
    request<CampaignOut>(`/media/campaigns/${id}`, { method: "PATCH", body }),
  deleteCampaign: (id: number) => request<void>(`/media/campaigns/${id}`, { method: "DELETE" }),
  campaignSummary: (id: number) => request<CampaignSummaryOut>(`/media/campaigns/${id}/summary`),
  campaignDeleteImpact: (id: number) => request<CampaignDeleteImpact>(`/media/campaigns/${id}/delete-impact`),

  // Ads
  listAds: (params: { campaign_id?: number; client_id?: number; ad_type?: string; q?: string; limit?: number; offset?: number } = {}) =>
    request<AdOut[]>(`/media/ads${qs(params)}`),
  createAd: (body: { campaign_id: number; title: string; ad_type: AdType; summary?: string;
    creative_url?: string; received_at?: string; requested_by_text?: string;
    category_id?: number; priority?: AdPriority; tags?: string[] }) =>
    request<AdOut>("/media/ads", { method: "POST", body }),
  getAd: (id: number) => request<AdOut>(`/media/ads/${id}`),
  patchAd: (id: number, body: Partial<AdOut>) =>
    request<AdOut>(`/media/ads/${id}`, { method: "PATCH", body }),
  discardAd: (id: number) => request<AdOut>(`/media/ads/${id}/discard`, { method: "POST" }),
  reactivateAd: (id: number) => request<AdOut>(`/media/ads/${id}/reactivate`, { method: "POST" }),
  deleteAd: (id: number) => request<void>(`/media/ads/${id}`, { method: "DELETE" }),

  // Placements
  listPlacements: (params: {
    ad_id?: number; outlet_id?: number; state?: string; coverage?: string;
    region_id?: number; client_id?: number; date_from?: string; date_to?: string;
    limit?: number; offset?: number;
  } = {}) => request<PlacementOut[]>(`/media/placements${qs(params)}`),
  createPlacement: (body: Partial<PlacementOut> & { ad_id: number; outlet_id: number; coverage: Coverage }) =>
    request<PlacementOut>("/media/placements", { method: "POST", body }),
  getPlacement: (id: number) => request<PlacementOut>(`/media/placements/${id}`),
  patchPlacement: (id: number, body: Partial<PlacementOut>) =>
    request<PlacementOut>(`/media/placements/${id}`, { method: "PATCH", body }),
  transitionPlacement: (id: number, body: { state: PlacementState; actual_end_at?: string; notes?: string }) =>
    request<PlacementOut>(`/media/placements/${id}/transition`, { method: "POST", body }),
  placementDeleteImpact: (id: number) =>
    request<PlacementDeleteImpact>(`/media/placements/${id}/delete-impact`),
  // Borrado IRREVERSIBLE (evidencias caen en cascada). Para conservar el
  // histórico usar transitionPlacement(id, { state: "cancelado" }).
  deletePlacement: (id: number) => request<void>(`/media/placements/${id}`, { method: "DELETE" }),
  recalculatePlacementCost: (id: number) =>
    request<PlacementOut>(`/media/placements/${id}/recalculate-cost`, { method: "POST" }),
  estimateOutletPrice: (outlet_id: number, body: { word_count?: number; spot_duration_seconds?: number; repetitions?: number }) =>
    request<PriceEstimateOut>(`/media/outlets/${outlet_id}/estimate-price`, { method: "POST", body }),
  exportPlacementsCsvUrl: (params: { client_id?: number; campaign_id?: number; state?: string; date_from?: string; date_to?: string } = {}) =>
    `/api/media/placements/export.csv${qs(params)}`,

  // Evidences (multipart)
  listEvidences: (params: { placement_id?: number; stage?: string; limit?: number; offset?: number } = {}) =>
    request<EvidenceOut[]>(`/media/evidences${qs(params)}`),
  uploadEvidence: async (placement_id: number, file: File, meta: EvidenceUploadMeta) => {
    const form = new FormData();
    form.append("placement_id", String(placement_id));
    form.append("stage", meta.stage);
    if (meta.caption) form.append("caption", meta.caption);
    if (meta.description) form.append("description", meta.description);
    if (meta.tags && meta.tags.length) form.append("tags", JSON.stringify(meta.tags));
    if (meta.is_public !== undefined) form.append("is_public", String(meta.is_public));
    if (meta.extra && Object.keys(meta.extra).length) form.append("extra", JSON.stringify(meta.extra));
    form.append("file", file);
    const resp = await fetch("/api/media/evidences", {
      method: "POST", credentials: "include", body: form,
    });
    if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
    return (await resp.json()) as EvidenceOut;
  },
  patchEvidence: (id: number, body: Partial<{ stage: string; caption: string; description: string; tags: string[]; is_public: boolean; extra: Record<string, unknown> }>) =>
    request<EvidenceOut>(`/media/evidences/${id}`, { method: "PATCH", body }),
  deleteEvidence: (id: number) => request<void>(`/media/evidences/${id}`, { method: "DELETE" }),

  // Catálogos ad_types + ad_categories
  listAdTypes: (params: { is_active?: boolean; q?: string } = {}) =>
    request<AdTypeOut[]>(`/media/ad-types${qs(params)}`),
  createAdType: (body: { code: string; name: string; description?: string; sort_order?: number }) =>
    request<AdTypeOut>("/media/ad-types", { method: "POST", body }),
  patchAdType: (id: number, body: Partial<{ code: string; name: string; description: string; is_active: boolean; sort_order: number }>) =>
    request<AdTypeOut>(`/media/ad-types/${id}`, { method: "PATCH", body }),
  deleteAdType: (id: number) => request<void>(`/media/ad-types/${id}`, { method: "DELETE" }),

  listAdCategories: (params: { is_active?: boolean; q?: string } = {}) =>
    request<AdCategoryOut[]>(`/media/ad-categories${qs(params)}`),
  createAdCategory: (body: { code: string; name: string; description?: string; color?: string; sort_order?: number }) =>
    request<AdCategoryOut>("/media/ad-categories", { method: "POST", body }),
  patchAdCategory: (id: number, body: Partial<{ code: string; name: string; description: string; color: string; is_active: boolean; sort_order: number }>) =>
    request<AdCategoryOut>(`/media/ad-categories/${id}`, { method: "PATCH", body }),
  deleteAdCategory: (id: number) => request<void>(`/media/ad-categories/${id}`, { method: "DELETE" }),

  // Reports
  listReports: (params: { client_id?: number; limit?: number; offset?: number } = {}) =>
    request<MediaReportOut[]>(`/media/reports${qs(params)}`),
  createReport: (body: { name: string; kind: ReportKind; format: ReportFormat; client_id?: number; filters?: Record<string, unknown>; branding?: Record<string, unknown>; period_label?: string; summary_markdown?: string }) =>
    request<MediaReportOut>("/media/reports", { method: "POST", body }),
  getReport: (id: number) => request<MediaReportOut>(`/media/reports/${id}`),
  patchReport: (id: number, body: Partial<{ name: string; kind: ReportKind; format: ReportFormat; client_id: number; filters: Record<string, unknown>; period_label: string; summary_markdown: string; cover_image_url: string; is_public: boolean }>) =>
    request<MediaReportOut>(`/media/reports/${id}`, { method: "PATCH", body }),
  deleteReport: (id: number) => request<void>(`/media/reports/${id}`, { method: "DELETE" }),
  runReport: (id: number) => request<MediaReportRunOut>(`/media/reports/${id}/run`, { method: "POST" }),
  getRun: (run_id: number) => request<MediaReportRunOut>(`/media/reports/runs/${run_id}`),
  downloadRunUrl: (run_id: number) => `/api/media/reports/runs/${run_id}/download`,
  shareRun: (run_id: number, expires_in_days = 90) =>
    request<MediaReportRunOut>(`/media/reports/runs/${run_id}/share`, { method: "POST", body: { expires_in_days } }),
  publishReport: (id: number, expires_in_days = 90) =>
    request<MediaReportOut>(`/media/reports/${id}/publish`, { method: "POST", body: { expires_in_days } }),
  unpublishReport: (id: number) =>
    request<MediaReportOut>(`/media/reports/${id}/unpublish`, { method: "POST" }),
  publicDownloadUrl: (share_token: string) => `/api/public/reports/${share_token}/download`,
  publicPortalUrl: (share_token: string) => `/public/reports/${share_token}`,

  // Report assets (archivos adjuntos al reporte)
  listReportAssets: (report_id: number) =>
    request<ReportAssetOut[]>(`/media/report-assets/by-report/${report_id}`),
  uploadReportAsset: async (report_id: number, file: File, meta: { kind: AssetKind; title: string; description?: string; is_public?: boolean; sort_order?: number; campaign_id?: number; ad_id?: number; placement_id?: number }) => {
    const form = new FormData();
    form.append("kind", meta.kind);
    form.append("title", meta.title);
    if (meta.description) form.append("description", meta.description);
    if (meta.is_public !== undefined) form.append("is_public", String(meta.is_public));
    if (meta.sort_order !== undefined) form.append("sort_order", String(meta.sort_order));
    if (meta.campaign_id !== undefined) form.append("campaign_id", String(meta.campaign_id));
    if (meta.ad_id !== undefined) form.append("ad_id", String(meta.ad_id));
    if (meta.placement_id !== undefined) form.append("placement_id", String(meta.placement_id));
    form.append("file", file);
    const resp = await fetch(`/api/media/report-assets/by-report/${report_id}`, {
      method: "POST", credentials: "include", body: form,
    });
    if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
    return (await resp.json()) as ReportAssetOut;
  },
  patchReportAsset: (asset_id: number, body: Partial<{ kind: AssetKind; title: string; description: string; is_public: boolean; sort_order: number; campaign_id: number | null; ad_id: number | null; placement_id: number | null; extra: Record<string, unknown> }>) =>
    request<ReportAssetOut>(`/media/report-assets/${asset_id}`, { method: "PATCH", body }),
  deleteReportAsset: (asset_id: number) =>
    request<void>(`/media/report-assets/${asset_id}`, { method: "DELETE" }),

  // Report indicators (KPIs manuales)
  listReportIndicators: (report_id: number) =>
    request<ReportIndicatorOut[]>(`/media/report-indicators/by-report/${report_id}`),
  createReportIndicator: (report_id: number, body: { kind: IndicatorKind; code: string; label: string; value_numeric?: number; value_text?: string; unit?: string; source?: string; measured_at?: string; sort_order?: number; notes?: string; is_public?: boolean; campaign_id?: number; ad_id?: number; placement_id?: number }) =>
    request<ReportIndicatorOut>(`/media/report-indicators/by-report/${report_id}`, { method: "POST", body }),
  patchReportIndicator: (id: number, body: Partial<{ kind: IndicatorKind; code: string; label: string; value_numeric: number; value_text: string; unit: string; source: string; measured_at: string; sort_order: number; notes: string; is_public: boolean; campaign_id: number | null; ad_id: number | null; placement_id: number | null }>) =>
    request<ReportIndicatorOut>(`/media/report-indicators/${id}`, { method: "PATCH", body }),
  deleteReportIndicator: (id: number) =>
    request<void>(`/media/report-indicators/${id}`, { method: "DELETE" }),

  // Portal público — acepta filtros opcionales. Si el cliente configuró
  // credenciales, portalFetch lanza PortalAuthRequiredError (401) → login.
  getPublicPortal: (share_token: string, filters: PortalFilters = {}) =>
    portalFetch<PublicReportPayload>(share_token, `/api/public/reports/${share_token}${portalQs(filters)}`),
  getPublicFilterOptions: (share_token: string) =>
    portalFetch<PortalFilterOptions>(share_token, `/api/public/reports/${share_token}/filter-options`),
  getPublicPlacements: (share_token: string, filters: PortalFilters = {}, opts: { limit?: number; offset?: number; sort?: string } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries({ ...filters, ...opts })) {
      if (v !== undefined && v !== "" && v !== null) sp.append(k, String(v));
    }
    return portalFetch<PortalPlacementsPage>(share_token, `/api/public/reports/${share_token}/placements?${sp.toString()}`);
  },
  getPublicAds: (share_token: string, filters: PortalFilters = {}, opts: { limit?: number; offset?: number; sort?: string } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries({ ...filters, ...opts })) {
      if (v !== undefined && v !== "" && v !== null) sp.append(k, String(v));
    }
    return portalFetch<PortalAdsPage>(share_token, `/api/public/reports/${share_token}/ads?${sp.toString()}`);
  },
  publicExportCsvUrl: (share_token: string, filters: PortalFilters = {}) =>
    withPortalKey(`/api/public/reports/${share_token}/export.csv${portalQs(filters)}`, share_token),
  getPublicGeoDetail: (share_token: string, level: "dep" | "prov" | "dist", parent: string | null, filters: PortalFilters = {}) => {
    const sp = new URLSearchParams();
    sp.append("level", level);
    if (parent) sp.append("parent", parent);
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== "" && v !== null) sp.append(k, String(v));
    }
    return portalFetch<{
      level: string; parent?: string;
      items: Array<{ ubigeo: string; name: string; placements: number; spending: number }>;
    }>(share_token, `/api/public/reports/${share_token}/geo-detail?${sp.toString()}`);
  },
  // Login del portal (cuando el cliente configuró credenciales)
  publicPortalLogin: async (share_token: string, body: { username?: string; password: string }) => {
    const r = await fetch(`/api/public/reports/${share_token}/login`, {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => null);
      throw new Error(typeof b?.detail === "string" ? b.detail : `HTTP ${r.status}`);
    }
    return r.json() as Promise<{ required: boolean; key: string | null; expires_in_hours?: number }>;
  },
};

// Helper interno: querystring
function qs(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.append(k, String(v));
  return `?${sp.toString()}`;
}

function portalQs(filters: PortalFilters): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "" && v !== null) sp.append(k, String(v));
  }
  return sp.toString() ? `?${sp.toString()}` : "";
}
